import { useMemo, memo, useCallback } from "react";
import type { CSSProperties } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { FormFieldDefinition, FormSectionDefinition } from "@rwaforms/shared";
import { useBuilderStore } from "../hooks/useBuilderStore";

interface FormCanvasProps {
  mode: "build" | "preview";
}

interface SectionGroup {
  section: FormSectionDefinition | null;
  fields: FormFieldDefinition[];
}

const SECTION_CLASS =
  "surface" as const;

const FormCanvas = memo(function FormCanvas({ mode }: FormCanvasProps): JSX.Element {
  const schema = useBuilderStore((state) => state.schema);
  const addSection = useBuilderStore((state) => state.addSection);
  const selectedFieldId = useBuilderStore((state) => state.selectedFieldId);
  const selectField = useBuilderStore((state) => state.selectField);
  const selectSection = useBuilderStore((state) => state.selectSection);

  // Memoize expensive grouping computation
  const groups = useMemo<SectionGroup[]>(() => {
    const sortedSections = [...schema.sections].sort((a, b) => a.position - b.position);
    const grouped: SectionGroup[] = sortedSections.map((section) => ({
      section,
      fields: schema.fields
        .filter((field) => field.sectionId === section.id)
        .sort((a, b) => a.position - b.position),
    }));

    const rootFields = schema.fields
      .filter((field) => !field.sectionId)
      .sort((a, b) => a.position - b.position);
    return [{ section: null, fields: rootFields }, ...grouped];
  }, [schema.sections, schema.fields]);

  const handleAddSection = useCallback(() => {
    addSection("New section");
  }, [addSection]);

  return (
    <div className="surface scroll-container" style={{ padding: "1.5rem" }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 600 }}>Form layout</h2>
        {mode === "build" && (
          <button
            className="button secondary"
            onClick={handleAddSection}
            type="button"
          >
            Add section
          </button>
        )}
      </header>
      <p style={{ color: "var(--color-text-muted)", marginTop: "0.25rem", marginBottom: "1.5rem" }}>
        Drag blocks from the palette or rearrange existing fields. Sections control logical groupings
        and branching.
      </p>

      <div style={{ display: "grid", gap: "1.25rem" }}>
        {groups.map((group) => (
          <SectionContainer
            key={group.section?.id ?? "root"}
            section={group.section}
            fields={group.fields}
            selectedFieldId={selectedFieldId}
            onSelectField={selectField}
            onSelectSection={selectSection}
            mode={mode}
          />
        ))}
      </div>
    </div>
  );
});

interface SectionContainerProps {
  section: FormSectionDefinition | null;
  fields: FormFieldDefinition[];
  selectedFieldId: string | null;
  onSelectField(fieldId: string): void;
  onSelectSection(sectionId: string | null): void;
  mode: "build" | "preview";
}

const SectionContainer = memo(function SectionContainer({
  section,
  fields,
  selectedFieldId,
  onSelectField,
  onSelectSection,
  mode,
}: SectionContainerProps): JSX.Element {
  const sectionId = section?.id ?? null;

  // Memoize droppable data to prevent unnecessary re-renders
  const droppableData = useMemo(
    () => ({
      type: "section" as const,
      sectionId,
      insertIndex: fields.length,
    }),
    [sectionId, fields.length]
  );

  const { setNodeRef, isOver } = useDroppable({
    id: `section:${sectionId ?? "root"}`,
    data: droppableData,
  });

  // Memoize field IDs for SortableContext
  const fieldIds = useMemo(
    () => fields.map((field) => `field:${field.id}`),
    [fields]
  );

  const handleSelectSection = useCallback(() => {
    if (section) {
      onSelectSection(section.id);
    }
  }, [section, onSelectSection]);

  return (
    <article
      ref={setNodeRef}
      className={SECTION_CLASS}
      style={{
        border: isOver ? "1px dashed var(--color-primary)" : "1px solid var(--color-border)",
        borderRadius: "var(--radius-base)",
        padding: "1.25rem",
        background: "var(--color-surface)",
        boxShadow: "var(--shadow-sm)",
      }}
    >
      {section ? (
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1rem",
          }}
        >
          <div>
            <button
              type="button"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                textAlign: "left",
                cursor: "pointer",
              }}
              onClick={handleSelectSection}
            >
              <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{section.title || "Untitled section"}</h3>
              {section.description && (
                <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {section.description}
                </p>
              )}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span className="pill">Section {section.position + 1}</span>
            <button
              type="button"
              className="button ghost"
              style={{
                padding: "0.25rem 0.5rem",
                fontSize: "0.75rem",
                color: "var(--color-error, #c33)"
              }}
              onClick={() => {
                if (window.confirm(`Delete "${section.title || "this section"}"? Fields will be moved to the intro area.`)) {
                  const store = useBuilderStore.getState();
                  store.removeSection(section.id);
                  store.selectSection(null);
                }
              }}
              title="Remove section"
            >
              Remove
            </button>
          </div>
        </header>
      ) : (
        <header style={{ marginBottom: "1rem" }}>
          <h3 style={{ fontSize: "0.95rem", fontWeight: 600, color: "var(--color-text-muted)" }}>
            Intro (no section)
          </h3>
        </header>
      )}
      <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
        <div
          style={{
            display: "grid",
            gap: "0.75rem",
            gridTemplateColumns: section?.layout === "two_column" ? "repeat(auto-fit, minmax(280px, 1fr))" : "1fr",
          }}
        >
          {fields.map((field, index) => (
            <SortableFieldCard
              key={field.id}
              field={field}
              index={index}
              selected={selectedFieldId === field.id}
              onSelect={() => onSelectField(field.id)}
              mode={mode}
            />
          ))}
          {fields.length === 0 && (
            <EmptyDropHint sectionLabel={section ? section.title || "section" : "form"} isOver={isOver} />
          )}
        </div>
      </SortableContext>
    </article>
  );
});

interface SortableFieldCardProps {
  field: FormFieldDefinition;
  index: number;
  selected: boolean;
  onSelect(): void;
  mode: "build" | "preview";
}

const FIELD_KIND_LABEL: Record<string, string> = {
  short_text: "Short answer",
  long_text: "Paragraph",
  number: "Number",
  multiple_choice: "Single choice",
  checkboxes: "Multiple choice",
  dropdown: "Dropdown",
  rating: "Rating",
  file_upload: "File upload",
  date: "Date",
  time: "Time",
  calculated: "Calculated",
  static_text: "Static content",
};

function SortableFieldCard({ field, index, selected, onSelect, mode }: SortableFieldCardProps): JSX.Element {
  const removeField = useBuilderStore((state) => state.removeField);
  const moveField = useBuilderStore((state) => state.moveField);
  const schema = useBuilderStore((state) => state.schema);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `field:${field.id}`,
    disabled: mode === "preview",
    data: {
      type: "field",
      fieldId: field.id,
      sectionId: field.sectionId ?? null,
      index,
    },
  });

  const handleRemove = useCallback(() => {
    removeField(field.id);
  }, [removeField, field.id]);

  // Get fields in the same section for reordering
  const sectionFields = useMemo(() => {
    return schema.fields
      .filter((f) => (f.sectionId ?? null) === (field.sectionId ?? null))
      .sort((a, b) => a.position - b.position);
  }, [schema.fields, field.sectionId]);

  const currentIndex = sectionFields.findIndex((f) => f.id === field.id);
  const canMoveUp = currentIndex > 0;
  const canMoveDown = currentIndex < sectionFields.length - 1;

  const handleMoveUp = useCallback(() => {
    if (canMoveUp) {
      moveField(field.id, field.sectionId ?? null, currentIndex - 1);
    }
  }, [canMoveUp, moveField, field.id, field.sectionId, currentIndex]);

  const handleMoveDown = useCallback(() => {
    if (canMoveDown) {
      moveField(field.id, field.sectionId ?? null, currentIndex + 1);
    }
  }, [canMoveDown, moveField, field.id, field.sectionId, currentIndex]);

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    border: selected ? "2px solid var(--color-primary)" : "1px solid var(--color-border)",
    borderRadius: "calc(var(--radius-base) - 2px)",
    padding: "1rem",
    background: "var(--color-surface)",
    cursor: mode === "build" ? "grab" : "default",
    boxShadow: selected ? "var(--shadow-md)" : "var(--shadow-sm)",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(mode === "build" ? listeners : undefined)}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "0.5rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            type="button"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              textAlign: "left",
              cursor: "pointer",
              width: "100%",
            }}
            onClick={onSelect}
          >
            <h4 style={{ margin: 0, fontSize: "1rem" }}>{field.title || "Untitled field"}</h4>
            <p style={{ margin: 0, fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
              {FIELD_KIND_LABEL[field.type] ?? field.type}
              {field.required ? " (required)" : ""}
            </p>
          </button>
        </div>
        {mode === "build" && (
          <div style={{ display: "flex", gap: "0.25rem", alignItems: "center", flexShrink: 0 }}>
            {/* Mobile reorder buttons */}
            <button
              type="button"
              className="button ghost mobile-only"
              style={{ padding: "0.25rem 0.5rem", fontSize: "0.9rem" }}
              onClick={handleMoveUp}
              disabled={!canMoveUp}
              title="Move up"
            >
              ▲
            </button>
            <button
              type="button"
              className="button ghost mobile-only"
              style={{ padding: "0.25rem 0.5rem", fontSize: "0.9rem" }}
              onClick={handleMoveDown}
              disabled={!canMoveDown}
              title="Move down"
            >
              ▼
            </button>
            <button
              type="button"
              className="button ghost"
              style={{ padding: "0.35rem 0.65rem", fontSize: "0.75rem" }}
              onClick={handleRemove}
            >
              Remove
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const EmptyDropHint = memo(function EmptyDropHint({
  sectionLabel,
  isOver,
}: {
  sectionLabel: string;
  isOver: boolean;
}): JSX.Element {
  const style = useMemo(
    () => ({
      padding: "1.25rem",
      borderRadius: "calc(var(--radius-base) - 2px)",
      border: isOver ? "2px dashed var(--color-primary)" : "1px dashed var(--color-border)",
      color: "var(--color-text-muted)",
      textAlign: "center" as const,
      background: "var(--color-primary-soft)",
    }),
    [isOver]
  );

  return (
    <div style={style}>
      Drop a field here to populate the {sectionLabel}.
    </div>
  );
});

export default FormCanvas;
